/**
 * The leaderboard — Blockari's `/leaderboard` (screens/Leaderboard.tsx), in
 * the kit's markup with the `w21-` prefix: five boards (XP, $CHAIN, best score, card
 * streaks, win streaks) over two periods (this week, all time), all ranked by the server
 * (`GET /api/leaderboard`). Rows are History's `.match` cards: the place, the
 * avatar, the badge and name over the rank line, the number right in the
 * display face. The top three are the podium (gold, silver, bronze), the
 * weekly podium shows the chest each place is on track for, and the caller's
 * own row is lit where it is and pinned to the panel's foot whenever it is out
 * of view. The list scrolls inside the panel, which fills the viewport, so the
 * page never moves when a tab changes. No sub-text anywhere.
 *
 * Also here: the home screen's "Top this week" card (Blockari's TopThisWeek).
 */

import type { Rarity } from '@/game/chests';
import {
  leaderboard as fetchLeaderboard,
  type LeaderboardBoard,
  type LeaderboardMe,
  type LeaderboardPeriod,
  type LeaderboardRow,
  type LeaderboardView,
  type RankView,
} from '@/net/api';
import { chestIcon, lockIcon } from '@/chest/glyphs';
import { el } from '@/ui/hud';
import { BOARD_OPTIONS, PERIODS, boardName, boardText, emptyBoard, weeklyChest } from '@/ui/leaderboard-data';
import { segmented } from '@/ui/segmented';
import {
  TopBar,
  coin,
  createScreen,
  hueOf,
  initialsOf,
  kitXpChipSm,
  rankBadgeCard,
  rankClass,
  rankName,
  type TopBarCallbacks,
} from '@/ui/screens';

/* --------------------------------------------------------------------------
   Data: one fetch per board and period, kept 30 s on the client (the server
   caches 60 s), so flipping tabs back is instant. Keyed on the account too:
   `me` is the caller's.
   -------------------------------------------------------------------------- */

const FRESH_MS = 30_000;
const cache = new Map<string, { view: LeaderboardView; at: number }>();

export async function loadBoard(
  board: LeaderboardBoard,
  period: LeaderboardPeriod,
  limit: number,
  account: string,
): Promise<LeaderboardView | null> {
  const key = `${board}:${period}:${limit}:${account}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < FRESH_MS) return hit.view;
  const r = await fetchLeaderboard(board, period, limit);
  if (!r.ok) return null;
  cache.set(key, { view: r.value, at: Date.now() });
  return r.value;
}

function cached(board: LeaderboardBoard, period: LeaderboardPeriod, limit: number, account: string): LeaderboardView | null {
  return cache.get(`${board}:${period}:${limit}:${account}`)?.view ?? null;
}

/* --------------------------------------------------------------------------
   Formatting
   -------------------------------------------------------------------------- */

/** "Resets in 3d 4h" — the countdown to Monday 00:00 UTC. */
export function resetLabel(resetsAt: string, now = Date.now()): string {
  const ms = Math.max(0, new Date(resetsAt).getTime() - now);
  const m = Math.floor(ms / 60_000);
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  if (d > 0) return `Resets in ${d}d ${h}h`;
  if (h > 0) return `Resets in ${h}h ${m % 60}m`;
  return `Resets in ${Math.max(1, m)}m`;
}

/** Monday 00:00 UTC after now: the chip's value before the first response. */
export function nextMonday(now = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  d.setUTCDate(d.getUTCDate() + (7 - ((d.getUTCDay() + 6) % 7)));
  return d.toISOString();
}

const SVG = 'http://www.w3.org/2000/svg';
function clockIcon(): SVGSVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('aria-hidden', 'true');
  const c = document.createElementNS(SVG, 'circle');
  c.setAttribute('cx', '12');
  c.setAttribute('cy', '12');
  c.setAttribute('r', '9');
  const p = document.createElementNS(SVG, 'path');
  p.setAttribute('d', 'M12 7v5l3 2');
  svg.append(c, p);
  return svg;
}

/** The reset chip: a label, not sub-text. Refreshed every 30 s while it is on screen. */
function resetChip(): { root: HTMLElement; set(resetsAt: string | null): void } {
  const root = el('span', 'w21-chip w21-lb-reset');
  const text = el('span');
  root.append(clockIcon(), text);
  let at: string | null = null;
  const paint = (): void => {
    root.hidden = at === null;
    if (at) text.textContent = resetLabel(at);
  };
  setInterval(() => {
    if (root.isConnected) paint();
  }, 30_000);
  return {
    root,
    set(resetsAt) {
      at = resetsAt;
      paint();
    },
  };
}

/** The board's number in the display face, with its unit as a caption. */
export function boardValue(board: LeaderboardBoard, value: number | null): HTMLElement {
  if (value === null) return el('span', 'w21-lb-val w21-none', '—');
  const { text, unit } = boardText(board, value);
  const cls = board === 'won' ? ` w21-won${value < 0 ? ' w21-down' : value > 0 ? ' w21-up' : ''}` : '';
  const v = el('span', `w21-lb-val${cls}`);
  v.append(el('b', undefined, text));
  if (unit) v.append(el('small', undefined, unit));
  return v;
}

function avatar(name: string): HTMLElement {
  const a = el('span', 'w21-avatar');
  a.style.setProperty('--hue', String(hueOf(name)));
  a.textContent = initialsOf(name);
  a.setAttribute('aria-hidden', 'true');
  return a;
}

/** A guest's label chip beside the name (a label, not sub-text). */
function guestChip(): HTMLElement {
  return el('span', 'w21-chip w21-lb-guest', 'Guest');
}

function identity(
  row: { username: string; xpLevel: number; rank: RankView; guest: boolean },
  you: boolean,
  after?: HTMLElement | null,
  /** The claim chip already says "guest": no second chip on that row. */
  claiming = false,
): HTMLElement {
  const id = el('span', `w21-lb-id ${rankClass(row.rank.index)}`);
  const name = el('span', 'w21-lb-name');
  name.append(rankBadgeCard(row.rank, 18), el('span', 'w21-pt-name', you ? 'You' : row.username));
  if (row.guest && !claiming) name.append(guestChip());
  if (after) name.append(after);
  const line = el('span', 'w21-lb-rank');
  const rn = rankName(row.rank);
  rn.classList.add('w21-rank-name');
  line.append(rn, kitXpChipSm(row.xpLevel, row.rank));
  id.append(name, line);
  return id;
}

function boardRow(
  row: LeaderboardRow,
  board: LeaderboardBoard,
  k: number,
  mine: boolean,
  chest: Rarity | null,
  onOpen: (username: string) => void,
  onSignUp: () => void,
): HTMLElement {
  const podium = row.position <= 3 ? ` w21-podium w21-p${row.position}` : '';
  const a = el('a', `w21-match w21-lb-row${podium}${mine ? ' w21-me' : ''}${row.guest && mine ? ' w21-claim' : ''}`) as HTMLAnchorElement;
  a.href = `#profile/${encodeURIComponent(row.username)}`;
  a.style.setProperty('--k', String(Math.min(k, 10)));
  a.setAttribute('aria-label', `${row.position}. ${row.username}${mine ? ' (you)' : ''}`);
  a.addEventListener('click', (e) => {
    e.preventDefault();
    onOpen(row.username);
  });
  let badge: HTMLElement | null = null;
  // A guest's place pays no chest, and their name is only reserved: others see none, the guest sees the way to claim.
  const claim = row.guest && mine;
  if (claim) {
    badge = claimChip(chest, onSignUp);
  } else if (chest && !row.guest) {
    badge = el('span', `w21-lb-chest w21-r-${chest}`);
    badge.title = `${chest} chest`;
    badge.setAttribute('aria-label', `${chest} chest`);
    badge.append(chestIcon());
  }
  a.append(el('span', 'w21-lb-pos', String(row.position)), avatar(row.username), identity(row, mine, badge, claim));
  if (board === 'won' && row.value > 0) {
    const c = coin();
    c.classList.add('w21-lb-coin');
    a.append(c);
  }
  a.append(boardValue(board, row.value));
  return a;
}

/**
 * A guest's own row, wherever it is: the name (and, on the podium, the chest) is
 * only reserved until there is an account. An action chip that opens the
 * sign-up sheet — the chest icon on a podium place, a padlock otherwise —
 * never a caption. (Blockari's `ClaimChip`.)
 */
function claimChip(chest: Rarity | null, onSignUp: () => void): HTMLElement {
  const chip = el('span', `w21-chip w21-lb-claim w21-r-${chest ?? 'name'}`);
  chip.setAttribute('role', 'button');
  chip.tabIndex = 0;
  chip.setAttribute('aria-label', chest ? `Sign up to claim your ${chest} chest` : 'Sign up to claim your name');
  chip.append(chest ? chestIcon() : lockIcon(), document.createTextNode('Sign up to claim'));
  const go = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    onSignUp();
  };
  chip.addEventListener('click', go);
  chip.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') go(e);
  });
  return chip;
}

/** My row pinned to the panel's foot: "You · #214 · 12,340 XP". */
function meRow(
  me: LeaderboardMe,
  board: LeaderboardBoard,
  chest: Rarity | null,
  onOpen: (username: string) => void,
  onSignUp: () => void,
): HTMLAnchorElement {
  const a = el('a', `w21-match w21-lb-row w21-me w21-lb-pin${me.guest ? ' w21-claim' : ''}`) as HTMLAnchorElement;
  a.href = `#profile/${encodeURIComponent(me.username)}`;
  a.setAttribute('aria-label', `You: ${me.position !== null ? `#${me.position}` : 'unranked'}`);
  a.addEventListener('click', (e) => {
    e.preventDefault();
    onOpen(me.username);
  });
  a.append(
    el('span', 'w21-lb-pos', me.position !== null ? `#${me.position}` : '—'),
    avatar(me.username),
    identity(me, true, me.guest ? claimChip(chest, onSignUp) : null, me.guest),
    boardValue(board, me.value),
  );
  return a;
}

/* --------------------------------------------------------------------------
   The screen
   -------------------------------------------------------------------------- */

export interface LeaderboardCallbacks {
  bar: TopBarCallbacks;
  onProfile(username: string): void;
  /** A guest's "Sign up to claim": the account screen. */
  onSignUp(): void;
}

export class LeaderboardScreen {
  readonly root: HTMLElement;
  readonly topBar: TopBar;
  #board: LeaderboardBoard = 'xp';
  #period: LeaderboardPeriod = 'week';
  #account = '';
  #seq = 0;
  readonly #reset = resetChip();
  readonly #boardSeg: ReturnType<typeof segmented<LeaderboardBoard>>;
  readonly #periodSeg: ReturnType<typeof segmented<LeaderboardPeriod>>;
  readonly #panel: HTMLElement;
  readonly #list: HTMLOListElement;
  #pin: HTMLAnchorElement | null = null;

  constructor(private readonly callbacks: LeaderboardCallbacks) {
    const { screen, panel } = createScreen('leaderboard', true, 'main');
    this.root = screen;
    this.root.setAttribute('aria-label', '21 Wild — leaderboard');
    this.topBar = new TopBar(callbacks.bar);
    screen.append(this.topBar.root);
    panel.className = 'w21-page';

    const page = el('div', 'w21-lb-page');
    const head = el('div', 'w21-lb-head');
    head.append(el('h2', undefined, 'Leaderboard'), this.#reset.root);
    const controls = el('div', 'w21-lb-controls');
    // `fit`: five pills on one row, each as wide as its label plus its padding (Blockari c258375).
    this.#boardSeg = segmented('Board', BOARD_OPTIONS, (b) => this.show(b, this.#period), { fit: true });
    this.#periodSeg = segmented('Period', PERIODS, (p) => this.show(this.#board, p));
    controls.append(this.#boardSeg.root, this.#periodSeg.root);
    this.#panel = el('section', 'w21-panel w21-lb-panel');
    this.#list = el('ol', 'w21-lb-list');
    this.#panel.append(this.#list);
    page.append(head, controls, this.#panel);
    panel.append(page);

    // The panel fills the viewport under the controls; the list scrolls inside it.
    addEventListener('resize', () => {
      this.#fit();
      this.#checkPin();
    });
    // Whenever the list's box changes (the fit, a font landing), the pin is judged again.
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(() => this.#checkPin()).observe(this.#list);
    this.#list.addEventListener('scroll', () => this.#checkPin(), { passive: true });
  }

  /** Whose `me` it is: the board is fetched again for another account. */
  setAccount(username: string | null): void {
    this.#account = username ?? '';
  }

  get board(): LeaderboardBoard {
    return this.#board;
  }

  get period(): LeaderboardPeriod {
    return this.#period;
  }

  show(board: LeaderboardBoard = this.#board, period: LeaderboardPeriod = this.#period): void {
    const changed = board !== this.#board || period !== this.#period;
    this.#board = board;
    this.#period = period;
    this.#boardSeg.set(board);
    this.#periodSeg.set(period);
    this.#reset.set(period === 'week' ? nextMonday() : null);
    requestAnimationFrame(() => {
      this.#fit();
      this.#checkPin();
    });
    const seq = ++this.#seq;
    const hit = cached(board, period, 100, this.#account);
    // A tab change never shows the previous board's rows for a frame.
    if (hit) this.#paint(hit);
    else this.#skeleton();
    if (changed) this.#list.scrollTop = 0;
    void loadBoard(board, period, 100, this.#account).then((view) => {
      if (seq !== this.#seq) return;
      if (view) {
        if (view !== hit) this.#paint(view);
      } else if (!hit) this.#failed();
    });
  }

  #fit(): void {
    if (!this.root.isConnected || this.root.hidden) return;
    const top = this.#panel.getBoundingClientRect().top - this.root.getBoundingClientRect().top + this.root.scrollTop;
    const page = this.#panel.closest('.w21-page');
    const below = page ? parseFloat(getComputedStyle(page).paddingBottom) || 0 : 16;
    this.#panel.style.height = `${Math.max(360, Math.floor(this.root.clientHeight - top - below))}px`;
  }

  #frame(): void {
    this.#panel.className = `w21-panel w21-lb-panel w21-board-${this.#board}`;
    this.#panel.setAttribute(
      'aria-label',
      `${boardName(this.#board)} · ${PERIODS.find((p) => p.value === this.#period)?.label ?? ''}`,
    );
    this.#pin?.remove();
    this.#pin = null;
  }

  #skeleton(): void {
    this.#frame();
    this.#panel.setAttribute('aria-busy', 'true');
    this.#list.replaceChildren(
      ...Array.from({ length: 9 }, (_, i) => {
        const li = el('li', `w21-skeleton w21-lb-skel${i < 3 ? ' w21-podium' : ''}`);
        li.style.animationDelay = `${i * 60}ms`;
        li.setAttribute('aria-hidden', 'true');
        return li;
      }),
    );
  }

  #failed(): void {
    this.#frame();
    this.#panel.removeAttribute('aria-busy');
    this.#list.replaceChildren(this.#emptyRow('Leaderboard unavailable'));
  }

  #emptyRow(text: string): HTMLElement {
    const li = el('li', 'w21-match w21-lb-row w21-lb-empty', text);
    return li;
  }

  #paint(view: LeaderboardView): void {
    this.#frame();
    this.#panel.removeAttribute('aria-busy');
    this.#reset.set(this.#period === 'week' ? (view.resetsAt ?? nextMonday()) : null);
    const me = view.me;
    const open = (u: string): void => this.callbacks.onProfile(u);
    if (view.rows.length === 0) {
      this.#list.replaceChildren(this.#emptyRow(emptyBoard(this.#board, this.#period)));
    } else {
      this.#list.replaceChildren(
        ...view.rows.map((r, k) => {
          const li = el('li');
          li.append(
            boardRow(
              r,
              this.#board,
              k,
              me?.position === r.position,
              weeklyChest(this.#board, this.#period, r.position, r.value),
              open,
              () => this.callbacks.onSignUp(),
            ),
          );
          return li;
        }),
      );
    }
    if (me) {
      this.#panel.classList.add('w21-has-me');
      this.#pin = meRow(me, this.#board, me.position !== null ? weeklyChest(this.#board, this.#period, me.position, me.value ?? 0) : null, open, () => this.callbacks.onSignUp());
      this.#panel.append(this.#pin);
    }
    this.#checkPin();
  }

  /**
   * Whether my own row is fully in view above the pinned slot. The pinned row
   * shows whenever it is not — scrolled away, or beyond the top 100.
   */
  #checkPin(): void {
    const pin = this.#pin;
    if (!pin) return;
    const row = this.#list.querySelector<HTMLElement>('.w21-lb-row.w21-me');
    let visible = false;
    if (row) {
      const r = this.#list.getBoundingClientRect();
      const b = row.getBoundingClientRect();
      const floor = r.bottom - pin.offsetHeight - 8;
      // My real row takes over the moment any of it shows above the pin's slot: never two at once.
      visible = b.bottom > r.top + 1 && b.top < floor - 1;
    }
    this.#panel.classList.toggle('w21-pin-out', visible);
    pin.classList.toggle('w21-out', visible);
    pin.setAttribute('aria-hidden', String(visible));
    pin.tabIndex = visible ? -1 : 0;
  }
}

/* --------------------------------------------------------------------------
   Home: "Top this week" — the XP weekly board's podium and my place
   -------------------------------------------------------------------------- */

export class TopThisWeek {
  readonly root: HTMLAnchorElement;
  readonly #reset = resetChip();
  readonly #list = el('ol', 'w21-lb-card-list');
  readonly #me = el('span', 'w21-lb-card-me');
  #seq = 0;

  constructor(onOpen: () => void) {
    this.root = el('a', 'w21-panel w21-lb-card') as HTMLAnchorElement;
    this.root.href = '#leaderboard';
    this.root.setAttribute('aria-label', 'Leaderboard: top this week');
    this.root.addEventListener('click', (e) => {
      e.preventDefault();
      onOpen();
    });
    const head = el('span', 'w21-lb-card-head');
    head.append(el('h3', undefined, 'Top this week'), this.#reset.root);
    this.#reset.set(nextMonday());
    this.#me.hidden = true;
    this.root.append(head, this.#list, this.#me);
    this.#list.replaceChildren(...[0, 1, 2].map(() => el('li', 'w21-skeleton w21-lb-card-skel')));
  }

  refresh(account: string | null): void {
    const seq = ++this.#seq;
    void loadBoard('xp', 'week', 3, account ?? '').then((view) => {
      if (seq !== this.#seq) return;
      this.root.hidden = view === null;
      if (view) this.#paint(view);
    });
  }

  #paint(view: LeaderboardView): void {
    this.#reset.set(view.resetsAt ?? nextMonday());
    const me = view.me;
    if (view.rows.length === 0) {
      this.#list.replaceChildren(el('li', 'w21-lb-card-row w21-empty', 'No games yet this week'));
    } else {
      this.#list.replaceChildren(
        ...view.rows.map((r) => {
          const li = el('li', `w21-lb-card-row w21-p${r.position}${me?.position === r.position ? ' w21-me' : ''}`);
          li.append(el('span', 'w21-lb-pos', String(r.position)), rankBadgeCard(r.rank, 18), el('span', 'w21-pt-name', r.username));
          if (r.guest) li.append(guestChip());
          li.append(boardValue('xp', r.value));
          return li;
        }),
      );
    }
    const inTop = me !== null && me.position !== null && me.position <= 3;
    this.#me.hidden = !me || inTop;
    if (me && !inTop) {
      this.#me.replaceChildren(
        el('span', 'w21-lb-pos', me.position !== null ? `#${me.position}` : '—'),
        rankBadgeCard(me.rank, 18),
        el('span', 'w21-pt-name', 'You'),
      );
      if (me.guest) this.#me.append(guestChip());
      this.#me.append(boardValue('xp', me.value));
    }
  }
}
